/* eslint-disable indent */
import { type QueryClient } from '@tanstack/react-query'

import { assessmentService } from '@/services/api/assessment.service'
import { type PaginatedResponse } from '@/types/api.types'
import type { Assessment, AssessmentListParams } from '@/types/assessment.types'

export interface AssessmentQueryKeyFactory {
  assessments: {
    all: () => readonly ['assessments']
    lists: () => readonly ['assessments', 'list']
    list: (
      params?: AssessmentListParams
    ) => readonly ['assessments', 'list', AssessmentListParams | undefined]
    detail: (assessmentId: string) => readonly ['assessments', 'detail', string]
  }
}

export const assessmentQueryKeys: AssessmentQueryKeyFactory = {
  assessments: {
    all: () => ['assessments'] as const,
    lists: () => ['assessments', 'list'] as const,
    list: (params) => ['assessments', 'list', params] as const,
    detail: (assessmentId) => ['assessments', 'detail', assessmentId] as const
  }
}

export interface AssessmentQueryUtils {
  invalidateAssessmentQueries: (queryClient: QueryClient, assessmentId?: string) => Promise<void>
  prefetchNextPage: (
    queryClient: QueryClient,
    currentPage: number,
    filters: AssessmentListParams
  ) => Promise<void>
  setAssessmentOptimistic: (
    queryClient: QueryClient,
    assessmentId: string,
    data: Partial<Assessment>
  ) => void
  removeAssessmentFromCache: (queryClient: QueryClient, assessmentId: string) => void
}

export const assessmentQueryUtils: AssessmentQueryUtils = {
  async invalidateAssessmentQueries(queryClient, assessmentId) {
    const promises = [
      queryClient.invalidateQueries({ queryKey: assessmentQueryKeys.assessments.lists() })
    ]

    if (assessmentId) {
      promises.push(
        queryClient.invalidateQueries({
          queryKey: assessmentQueryKeys.assessments.detail(assessmentId)
        })
      )
    }

    await Promise.all(promises)
  },

  async prefetchNextPage(queryClient, currentPage, filters) {
    const nextPageFilters = { ...filters, page: currentPage + 1 }

    await queryClient.prefetchQuery({
      queryKey: assessmentQueryKeys.assessments.list(nextPageFilters),
      queryFn: () => assessmentService.getAssessments(nextPageFilters),
      staleTime: 1000 * 60 * 5 // 5 minutes
    })
  },

  setAssessmentOptimistic(queryClient, assessmentId, data) {
    // Update assessment detail cache
    queryClient.setQueryData(
      assessmentQueryKeys.assessments.detail(assessmentId),
      (old: Assessment | undefined) => {
        if (!old) return old
        return { ...old, ...data }
      }
    )

    // Update assessment in lists cache
    queryClient.setQueriesData(
      { queryKey: assessmentQueryKeys.assessments.lists(), exact: false },
      (old: PaginatedResponse<Assessment> | undefined) => {
        if (!old?.data) return old

        return {
          ...old,
          data: old.data.map((assessment: Assessment) =>
            assessment.id === assessmentId ? { ...assessment, ...data } : assessment
          )
        }
      }
    )
  },

  removeAssessmentFromCache(queryClient, assessmentId) {
    // Remove from detail cache
    queryClient.removeQueries({
      queryKey: assessmentQueryKeys.assessments.detail(assessmentId)
    })

    // Remove from lists cache
    queryClient.setQueriesData(
      { queryKey: assessmentQueryKeys.assessments.lists(), exact: false },
      (old: PaginatedResponse<Assessment> | undefined) => {
        if (!old?.data) return old

        return {
          ...old,
          data: old.data.filter((assessment: Assessment) => assessment.id !== assessmentId),
          meta: old.meta
            ? {
                ...old.meta,
                pagination: {
                  ...old.meta.pagination,
                  total: Math.max(0, old.meta.pagination.total - 1)
                }
              }
            : old.meta
        }
      }
    )
  }
}
